import { createSlice, type PayloadAction, type SliceCaseReducers } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { ApiError } from '../../apis/api';
import { getApiKey, updateApiKey } from '../../apis/enterprise';
import { DEFAULT_ERROR } from '../../const/messageCodes';
import { ApiKey } from '../../models/ApiKey';
import { AppDispatch, AppThunk } from '../store';

export interface ApiKeyState {
  isLoading: boolean;
  apiKey: ApiKey | null;
  error: null | string | ApiError;
}

export const apiKeySlice = createSlice<ApiKeyState, SliceCaseReducers<ApiKeyState>, 'apiKey'>({
  name: 'apiKey',
  initialState: {
    isLoading: false,
    apiKey: null,
    error: null,
  },
  reducers: {
    apiKeyRequested(state) {
      state.isLoading = true;
      state.error = null;
    },
    apiKeyReceived(state, { payload }: PayloadAction<ApiKey>) {
      state.apiKey = payload;
      state.isLoading = false;
      state.error = null;
    },
    errorReceived(state, { payload }: PayloadAction<string>) {
      state.error = payload;
      state.isLoading = false;
    },
    errorCleared(state) {
      state.error = null;
    },
    clearUnencryptedKey(state) {
      if (state.apiKey) {
        state.apiKey.unencryptedKey = undefined;
      }
    },
  },
});

export const { reducer: apiKeyReducer } = apiKeySlice;

const { apiKeyRequested, apiKeyReceived, errorReceived, errorCleared } = apiKeySlice.actions;

export { errorCleared };

const handleError = (error: AxiosError, dispatch: AppDispatch) => {
  if (!error.response) {
    return dispatch(errorReceived({ messageCode: DEFAULT_ERROR }));
  }
};

export const fetchApiKey =
  (enterpriseId: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(apiKeyRequested(undefined));
      const apiKey = await getApiKey(enterpriseId);
      dispatch(apiKeyReceived(apiKey));
    } catch (e: any) {
      handleError(e as AxiosError, dispatch);
    }
  };

export const resetApiKey =
  (enterpriseId: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(apiKeyRequested(undefined));
      const apiKey = await updateApiKey(enterpriseId);
      dispatch(apiKeyReceived(apiKey));
    } catch (e: any) {
      handleError(e as AxiosError, dispatch);
    }
  };
